// The sync log. Everything a person does is an append-only event; screens read a
// projection of pack + events. The same events are what the worker stores and relays.
import { z } from 'zod'
import { AgeBand, Figure, Pin, PersonKind } from './family'

export const Role = z.enum(['organiser', 'player'])
export type Role = z.infer<typeof Role>

export const Person = z.object({
  id: z.string(),
  name: z.string(),
  /** Kept for older code: child → player, admin/adult → organiser. */
  role: Role,
  kind: PersonKind,
  ageBand: AgeBand.nullable(),
  figure: Figure.nullable(),
  /** Signing in as this person needs a PIN. */
  pin: z.boolean(),
  /**
   * Plays the game: earns plays, wins prizes, has a Learn goal (WI-37). Every child does; a
   * grown-up does when they choose to, and a solo traveller always does. Missing on older
   * servers and cached lists: read it with `playsOf`.
   */
  plays: z.boolean().optional(),
})
export type Person = z.infer<typeof Person>

/** Whether someone plays (WI-37); a person from before the flag existed plays if they're a child. */
export const playsOf = (p: { kind: PersonKind; plays?: boolean | undefined }): boolean => p.plays ?? p.kind === 'child'

export const MAX_PAYLOAD_BYTES = 32 * 1024
export const MAX_EVENTS_PER_PUSH = 200
export const MAX_EVENTS_PER_PULL = 500

/** As written by a device. `personId` is stamped by the server from the session, never trusted from the client. */
export const ClientEvent = z.object({
  id: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'ULID'),
  type: z.string().regex(/^[a-z][a-zA-Z0-9.]{0,63}$/),
  at: z.number().int().positive(),
  deviceId: z.string().min(8).max(64),
  payload: z.unknown(),
  /** The trip it was made in (D1); missing on events from before trips existed. */
  tripId: z.string().regex(/^[a-z0-9][a-z0-9-]*$/).max(64).optional(),
})
export type ClientEvent = z.infer<typeof ClientEvent>

export const ServerEvent = ClientEvent.extend({
  seq: z.number().int().positive(),
  personId: z.string(),
})
export type ServerEvent = z.infer<typeof ServerEvent>

export const SyncRequest = z.object({
  since: z.number().int().nonnegative(),
  events: z.array(ClientEvent).max(MAX_EVENTS_PER_PUSH),
})
export type SyncRequest = z.infer<typeof SyncRequest>

export const SyncResponse = z.object({
  /** Ids of pushed events now stored (new or already there). */
  accepted: z.array(z.string()),
  /** Ids rejected, with a reason; the client drops these from its outbox. */
  rejected: z.array(z.object({ id: z.string(), reason: z.string() })),
  events: z.array(ServerEvent),
  cursor: z.number().int().nonnegative(),
  more: z.boolean(),
})
export type SyncResponse = z.infer<typeof SyncResponse>

export const SessionRequest = z.object({ personId: z.string().min(1), deviceId: z.string().min(8).max(64), pin: Pin.optional() })

/**
 * POST /api/session's answer (WI-17): the person, plus whose phone this is and whether this is
 * parent mode (a grown-up on a child's phone: a short session, and a way back to the child).
 */
export type SessionResponse = Person & { resident: string | null; parentMode: boolean }
